import { useCallback, useMemo, useRef, useState } from "react";

import { MultiSelectDropdown } from "#components/MultiSelectDropdown";
import { MultiSelectField, type MultiSelectFieldItem } from "#components/MultiSelectField";

import type {
  MultiSelectComboBoxBaseProps,
  MultiSelectComboBoxOption,
} from "./MultiSelectComboBox.types";

export interface MultiSelectComboBoxWithCTAProps extends MultiSelectComboBoxBaseProps {
  /**
   * Override: Callback fired when the user clicks Apply.
   * Selections are staged internally and only committed through this callback.
   */
  onChange?: (selected: MultiSelectComboBoxOption[]) => void;

  // --- CTA labels ---
  /** Label for the "Select All" button. Defaults to "Select All" */
  selectAllLabel?: string;
  /** Label for the "Clear All" button. Defaults to "Clear All" */
  clearAllLabel?: string;
  /** Label for the "Cancel" button. Defaults to "Cancel" */
  cancelLabel?: string;
  /** Label for the "Apply" button. Defaults to "Apply" */
  applyLabel?: string;
}

/**
 * MultiSelectComboBoxWithCTA - A multi-select combobox with Select All / Clear All
 * and Cancel / Apply action bars.
 *
 * Selections are **staged** internally while the dropdown is open. The consumer's
 * `onChange` is only called when the user clicks **Apply**. Clicking **Cancel**
 * (or closing the dropdown) discards any staged changes.
 *
 * @example Uncontrolled
 * ```tsx
 * <MultiSelectComboBoxWithCTA
 *   label="Providers"
 *   placeholder="Select providers..."
 *   options={providers}
 *   onChange={(selected) => console.log("applied:", selected)}
 * />
 * ```
 *
 * @example Controlled with search
 * ```tsx
 * <MultiSelectComboBoxWithCTA
 *   label="Providers"
 *   options={providers}
 *   value={selectedOptions}
 *   onChange={setSelectedOptions}
 *   withSearch
 * />
 * ```
 */
export function MultiSelectComboBoxWithCTA({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  label,
  placeholder = "Select options...",
  tooltipText,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  withSearch = false,
  searchPlaceholder,
  onSearch: externalSearch,
  selectAllLabel = "Select All",
  clearAllLabel = "Clear All",
  cancelLabel = "Cancel",
  applyLabel = "Apply",
  placement = "bottom",
  className,
  showClearAll = false,
}: MultiSelectComboBoxWithCTAProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<MultiSelectComboBoxOption[]>(
    defaultValue ?? [],
  );
  // The committed (confirmed) selection
  const committedOptions = isControlled ? controlledValue : internalValue;

  // Staged selection — what the user is toggling while the dropdown is open
  const [stagedOptions, setStagedOptions] = useState<MultiSelectComboBoxOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Snapshot of committedOptions when the dropdown opens, used to restore on cancel
  const snapshotRef = useRef<MultiSelectComboBoxOption[]>(committedOptions);

  // Tags in the trigger always reflect the committed selection — built directly, no lookup
  const committedItems: MultiSelectFieldItem[] = useMemo(
    () =>
      committedOptions.map((opt) => ({
        id: opt.id,
        name: opt.searchLabel ?? (typeof opt.name === "string" ? opt.name : opt.id),
        searchLabel: opt.searchLabel,
      })),
    [committedOptions],
  );

  const filteredOptions: MultiSelectComboBoxOption[] = useMemo(() => {
    if (externalSearch || !withSearch || !searchTerm) return options;

    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const searchText = opt.searchLabel ?? (typeof opt.name === "string" ? opt.name : opt.id);
      return searchText.toLowerCase().includes(lowerSearch);
    });
  }, [options, withSearch, searchTerm, externalSearch]);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      externalSearch?.(term);
    },
    [externalSearch],
  );

  const commitSelection = useCallback(
    (opts: MultiSelectComboBoxOption[]) => {
      if (!isControlled) {
        setInternalValue(opts);
      }
      onChange?.(opts);
    },
    [isControlled, onChange],
  );

  const handleApply = useCallback(() => {
    commitSelection(stagedOptions);
  }, [stagedOptions, commitSelection]);

  const handleCancel = useCallback(() => {
    setStagedOptions(snapshotRef.current);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isReadOnly && open) return;

      if (open) {
        // Snapshot the current committed state and seed staged with it
        const current = isControlled ? controlledValue : internalValue;
        snapshotRef.current = current;
        setStagedOptions(current);
      } else {
        // Closing without Apply → revert staged to snapshot
        setStagedOptions(snapshotRef.current);
        setSearchTerm("");
        externalSearch?.("");
      }
      setIsOpen(open);
    },
    [isControlled, controlledValue, internalValue, externalSearch, isReadOnly],
  );

  const handleTagRemove = useCallback(
    (items: MultiSelectFieldItem[]) => {
      const remainingIds = new Set(items.map((i) => i.id));
      commitSelection(committedOptions.filter((o) => remainingIds.has(o.id)));
    },
    [committedOptions, commitSelection],
  );

  const handleSelectAll = useCallback(() => {
    setStagedOptions(options);
  }, [options]);

  const handleClearAll = useCallback(() => {
    setStagedOptions([]);
  }, []);

  return (
    <MultiSelectDropdown
      bottomCTA={{
        left: {
          children: cancelLabel,
          variant: "tertiary",
          closeOnClick: true,
          onPress: handleCancel,
        },
        right: {
          children: applyLabel,
          variant: "primary",
          closeOnClick: true,
          onPress: handleApply,
        },
      }}
      isOpen={isOpen}
      label={label ?? ""}
      onChange={setStagedOptions}
      onOpenChange={handleOpenChange}
      onSearch={withSearch ? handleSearch : undefined}
      options={filteredOptions}
      placement={placement}
      searchPlaceholder={searchPlaceholder}
      searchValue={searchTerm}
      topCTA={{
        left: {
          children: selectAllLabel,
          onPress: handleSelectAll,
        },
        right: {
          children: clearAllLabel,
          onPress: handleClearAll,
        },
      }}
      trigger={
        <MultiSelectField
          assistiveText={assistiveText}
          className={className}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isOpen={isOpen}
          isReadOnly={isReadOnly}
          label={label}
          onChange={handleTagRemove}
          placeholder={placeholder}
          showClearAll={showClearAll}
          tooltipText={tooltipText}
          value={committedItems}
        />
      }
      value={stagedOptions}
      matchTriggerWidth
    />
  );
}
